import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  FiGlobe, FiSave, FiPlus, FiTrash2, FiSearch, FiMapPin, 
  FiLink, FiEye, FiRefreshCw, FiChevronDown, FiChevronUp
} from 'react-icons/fi';

const API_URL = getApiUrl();

export default function SitemapSettingsPage() {
  const [sitemapData, setSitemapData] = useState({
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    og_image: '',
    pages: [],
    seo_cities: [],
    indexed_pages: [],
    production_url: 'https://www.justasmile-events.com'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    meta: true,
    pages: true,
    cities: false
  });

  useEffect(() => {
    loadSitemapData();
  }, []);

  const loadSitemapData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load site data
      const siteRes = await fetch(`${API_URL}/api/site-builder/site`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (siteRes.ok) {
        const siteData = await siteRes.json();
        setSitemapData(prev => ({
          ...prev,
          seo_title: siteData.seo?.title || siteData.site_name || '',
          seo_description: siteData.seo?.description || '',
          seo_keywords: siteData.seo?.keywords || '',
          og_image: siteData.seo?.og_image || '',
          pages: siteData.pages || [],
          seo_cities: siteData.site_settings?.seo_cities || []
        }));
      }
      
      // Load indexed pages (from settings)
      const settingsRes = await fetch(`${API_URL}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSitemapData(prev => ({
          ...prev,
          indexed_pages: settingsData.indexed_pages || []
        }));
      }
    } catch (error) {
      console.error('Error loading sitemap data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Save SEO data to site builder
      const response = await fetch(`${API_URL}/api/site-builder/site`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          seo: {
            title: sitemapData.seo_title,
            description: sitemapData.seo_description,
            keywords: sitemapData.seo_keywords,
            og_image: sitemapData.og_image
          }
        })
      });

      if (response.ok) {
        toast.success('Paramètres du sitemap enregistrés');
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Generate sitemap preview
  const generateSitemapPreview = () => {
    // Use production URL - hardcoded for production deployment
    const baseUrl = sitemapData.production_url || 'https://www.justasmile-events.com';
    const urls = [];
    
    // Home page
    urls.push({ loc: baseUrl, priority: '1.0', changefreq: 'daily' });
    
    // Static pages
    const staticPages = ['contact', 'tarifs', 'galerie', 'a-propos', 'blog'];
    staticPages.forEach(page => {
      urls.push({ 
        loc: `${baseUrl}/${page}`, 
        priority: '0.8', 
        changefreq: 'weekly' 
      });
    });
    
    // SEO city pages
    sitemapData.seo_cities?.forEach(city => {
      const slug = city.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');
      urls.push({
        loc: `${baseUrl}/seo/photobooth-${slug}`,
        priority: '0.6',
        changefreq: 'monthly'
      });
    });
    
    // Custom pages
    sitemapData.pages?.forEach(page => {
      if (!page.isHome) {
        urls.push({
          loc: `${baseUrl}/${page.slug}`,
          priority: '0.7',
          changefreq: 'monthly'
        });
      }
    });
    
    return urls;
  };

  const sitemapUrls = generateSitemapPreview();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sitemap-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sitemap & SEO</h1>
          <p className="text-slate-500 mt-1">Configurez votre plan de site pour Google</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.open('/sitemap.xml', '_blank')}>
            <FiEye className="mr-2 h-4 w-4" />
            Voir sitemap.xml
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
            <FiSave className="mr-2 h-4 w-4" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {/* Meta Tags Section */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => toggleSection('meta')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiSearch className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle>Balises Meta (SEO Global)</CardTitle>
                <CardDescription>Ces informations apparaissent dans les résultats Google</CardDescription>
              </div>
            </div>
            {expandedSections.meta ? <FiChevronUp /> : <FiChevronDown />}
          </div>
        </CardHeader>
        {expandedSections.meta && (
          <CardContent className="space-y-4">
            <div>
              <Label>Titre du site (Title Tag)</Label>
              <Input
                value={sitemapData.seo_title}
                onChange={(e) => setSitemapData({ ...sitemapData, seo_title: e.target.value })}
                placeholder="Just A Smile Events - Location PhotoBooth & VideoBooth 360"
                className="mt-1"
                maxLength={60}
              />
              <p className="text-xs text-slate-500 mt-1">
                {sitemapData.seo_title?.length || 0}/60 caractères (recommandé: 50-60)
              </p>
            </div>

            <div>
              <Label>Description (Meta Description)</Label>
              <Textarea
                value={sitemapData.seo_description}
                onChange={(e) => setSitemapData({ ...sitemapData, seo_description: e.target.value })}
                placeholder="Location de photobooth, videobooth 360° et livre d'or vidéo pour vos événements : mariages, anniversaires, entreprises..."
                rows={3}
                className="mt-1"
                maxLength={160}
              />
              <p className="text-xs text-slate-500 mt-1">
                {sitemapData.seo_description?.length || 0}/160 caractères (recommandé: 150-160)
              </p>
            </div>

            <div>
              <Label>Mots-clés (Keywords)</Label>
              <Input
                value={sitemapData.seo_keywords}
                onChange={(e) => setSitemapData({ ...sitemapData, seo_keywords: e.target.value })}
                placeholder="photobooth, videobooth 360, location photomaton, mariage, événement"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Image Open Graph (partage réseaux sociaux)</Label>
              <Input
                value={sitemapData.og_image}
                onChange={(e) => setSitemapData({ ...sitemapData, og_image: e.target.value })}
                placeholder="https://votre-site.com/images/og-image.jpg"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Image affichée lors du partage sur Facebook, LinkedIn, etc. (1200x630 recommandé)
              </p>
            </div>

            {/* Google Preview */}
            <div className="mt-6 p-4 bg-white border rounded-xl">
              <p className="text-xs text-slate-500 mb-3">Aperçu Google :</p>
              <div className="space-y-1">
                <p className="text-blue-700 text-lg hover:underline cursor-pointer">
                  {sitemapData.seo_title || 'Titre de votre site'}
                </p>
                <p className="text-green-700 text-sm">
                  {sitemapData.production_url || 'https://www.justasmile-events.com'}
                </p>
                <p className="text-slate-600 text-sm">
                  {sitemapData.seo_description || 'Description de votre site...'}
                </p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Pages Section */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => toggleSection('pages')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiLink className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle>Pages indexées ({sitemapUrls.length})</CardTitle>
                <CardDescription>Liste des URLs qui seront incluses dans le sitemap</CardDescription>
              </div>
            </div>
            {expandedSections.pages ? <FiChevronUp /> : <FiChevronDown />}
          </div>
        </CardHeader>
        {expandedSections.pages && (
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {sitemapUrls.map((url, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FiGlobe className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm text-slate-700 truncate">{url.loc}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className="text-xs">
                      Priorité: {url.priority}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {url.changefreq}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            
            {sitemapUrls.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <FiGlobe className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Aucune page indexée</p>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Cities Section */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => toggleSection('cities')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FiMapPin className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <CardTitle>Pages SEO Villes ({sitemapData.seo_cities?.length || 0})</CardTitle>
                <CardDescription>Pages locales générées automatiquement</CardDescription>
              </div>
            </div>
            {expandedSections.cities ? <FiChevronUp /> : <FiChevronDown />}
          </div>
        </CardHeader>
        {expandedSections.cities && (
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {sitemapData.seo_cities?.map((city, index) => (
                <Badge key={index} variant="outline" className="py-1.5 px-3">
                  {city}
                </Badge>
              ))}
            </div>
            
            {(!sitemapData.seo_cities || sitemapData.seo_cities.length === 0) && (
              <p className="text-sm text-slate-500">
                Aucune ville configurée. Allez dans <strong>Pages SEO</strong> pour ajouter des villes.
              </p>
            )}
          </CardContent>
        )}
      </Card>

      {/* Sitemap XML Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiGlobe className="text-amber-600" />
            Aperçu du Sitemap XML
          </CardTitle>
          <CardDescription>
            Ce fichier est automatiquement généré et accessible à /sitemap.xml
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
            <pre className="text-xs font-mono">
{`<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${sitemapUrls.slice(0, 5).map(url => `  <url>
    <loc>${url.loc}</loc>
    <changefreq>${url.changefreq}</changefreq>
    <priority>${url.priority}</priority>
  </url>`).join('\n')}
  ${sitemapUrls.length > 5 ? `<!-- ... et ${sitemapUrls.length - 5} autres URLs -->` : ''}
</urlset>`}
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FiSearch className="text-xl text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">
                Comment soumettre votre sitemap à Google ?
              </h4>
              <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                <li>
                  Connectez-vous à <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">Google Search Console</a>
                </li>
                <li>Ajoutez votre site si ce n'est pas déjà fait</li>
                <li>Allez dans <strong>Sitemaps</strong> dans le menu de gauche</li>
                <li>Entrez l'URL de votre sitemap : <code className="bg-blue-100 px-2 py-0.5 rounded">/sitemap.xml</code></li>
                <li>Cliquez sur <strong>Envoyer</strong></li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
